import { type ReactNode } from 'react'
import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { type IResponseFilters } from 'entities/Contacts/api/filterTypes'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IExcludeFilter, type IFilterGroup } from 'widgets/FilterEditor/types/types'
import { FilterGroups, FiltersConfig } from 'widgets/FilterEditor/model'
import { checkHubspotList } from 'widgets/FilterEditor/integration/segment'

export interface IFilterStoreProps {
  makeRequest: () => Promise<IResponseFilters>
  mutateFilterGroups?: (groups: FilterGroups, filterGroups: IFilterGroup[]) => IFilterGroup[]
  showSpawned?: boolean
  viewOnly?: boolean
  excludeFilters?: IExcludeFilter | null
  searchPlaceholderLowerCase?: boolean
  placeholderPrefixMap?: Record<string, string>
}

export class FilterEditorStore {
  private _loading = true
  private _viewOnly = false
  private _showSpawned = false
  private _shouldShowAlert = false
  private _searchPlaceholderLowerCase = false
  private _excludeFilters: IExcludeFilter | null = null

  private readonly _mutateFilterGroups: NonNullable<
    IFilterStoreProps['mutateFilterGroups']
  > | null = null
  private readonly _makeRequest: NonNullable<IFilterStoreProps['makeRequest']>

  private _futureData: Promise<IResponseFilters> | null = null
  private _blockReason: ReactNode = null

  private _config = new FiltersConfig()
  private readonly _groups: FilterGroups

  constructor(props: IFilterStoreProps, private _entities: IEntitiesStore) {
    const placeholderPrefixMap = props.placeholderPrefixMap || {}

    this._groups = new FilterGroups(this._config, placeholderPrefixMap, this._entities)
    this._makeRequest = props.makeRequest
    this._mutateFilterGroups = props.mutateFilterGroups ?? null
    this._excludeFilters = props.excludeFilters ?? null
    this._viewOnly = props.viewOnly ?? false
    this._showSpawned = props.showSpawned ?? this._showSpawned
    this._searchPlaceholderLowerCase = props.searchPlaceholderLowerCase ?? false

    makeAutoObservable(this)
  }

  get loading() {
    return this._loading
  }

  get config() {
    return this._config
  }

  get filterGroups() {
    const data =
      this._mutateFilterGroups?.(this._groups, this._config.filterGroups) ??
      this._config.filterGroups

    if (!this._excludeFilters) return data

    return data.map((group) => {
      const excludeGroup = this._excludeFilters?.get(group.category)

      if (excludeGroup) {
        return {
          ...group,
          items: group.items.filter((item) => !excludeGroup.includes(item.id)),
        }
      }

      return group
    })
  }

  get filterGroupsOrigin() {
    return this._config.filterGroups
  }

  get groups() {
    return this._groups
  }

  get viewOnly() {
    return this._viewOnly
  }

  get showSpawned() {
    return this._showSpawned
  }

  get isReady() {
    return !this._groups.isEmpty && this._groups.isReady
  }

  get blockReason() {
    return this._blockReason
  }

  get isHubspotList() {
    return checkHubspotList(this._groups)
  }

  get shouldShowAlert() {
    return this._shouldShowAlert
  }

  get searchPlaceholderLowerCase() {
    return this._searchPlaceholderLowerCase
  }

  initFilters = async () => {
    if (this._futureData) return this._futureData.then()

    this._loading = true
    this._futureData = this._makeRequest()

    try {
      const data = await this._futureData
      runInAction(() => {
        this._config.populate(data)
        this._loading = false
      })
    } catch (error) {
      logger.error(error)

      runInAction(() => {
        this._futureData = null
        this._loading = false
      })
    }
  }

  setViewOnly(value: boolean) {
    this._viewOnly = value
  }

  setBlock(reason: React.ReactNode) {
    this._blockReason = reason
  }

  removeBlock() {
    this._blockReason = null
  }

  setShowLast(showLast: boolean) {
    this._showSpawned = showLast
  }

  clearFutureData = () => {
    this._futureData = null
  }

  dispose = () => {
    this.groups.clear()
  }

  setShouldShowAlert(value: boolean) {
    this._shouldShowAlert = value
  }
}
